import { Workflow, WorkflowStepType, isApprovalConfig, isAutoAssignConfig } from './types';

// ── Validation Types ───────────────────────────────────────────────

export interface ValidationErrors {
  name?: string;
  trigger?: string;
  steps?: string;
  conditions?: Record<number, { field?: string; operator?: string; value?: string }>;
  stepErrors?: Record<number, { name?: string; configuration?: string }>;
}

// ── Pure Validation ────────────────────────────────────────────────

export const validateWorkflow = (workflow: Workflow): ValidationErrors => {
  const errors: ValidationErrors = {};

  if (!workflow.name.trim()) errors.name = 'Workflow name is required';
  if (!workflow.trigger) errors.trigger = 'Trigger is required';
  if (workflow.steps.length === 0) errors.steps = 'At least one step is required';

  // Condition validation
  const conditionErrors: ValidationErrors['conditions'] = {};
  workflow.conditions.forEach((cond, i) => {
    const row: { field?: string; operator?: string; value?: string } = {};
    if (!cond.field) row.field = 'Field is required';
    if (!cond.operator) row.operator = 'Operator is required';
    if (!cond.value?.trim()) row.value = 'Value is required';
    if (Object.keys(row).length > 0) conditionErrors[i] = row;
  });
  if (Object.keys(conditionErrors).length > 0) errors.conditions = conditionErrors;

  // Step validation
  const stepErrors: ValidationErrors['stepErrors'] = {};
  workflow.steps.forEach((step, i) => {
    const row: { name?: string; configuration?: string } = {};
    if (!step.name.trim()) row.name = 'Step name is required';

    if (step.stepType === WorkflowStepType.ApprovalStep && isApprovalConfig(step.configuration)) {
      const cfg = step.configuration;
      if (!cfg.approverEmail?.trim())
        row.configuration = 'Approver is required';
    }

    if (step.stepType === WorkflowStepType.AutoAssignmentStep && isAutoAssignConfig(step.configuration)) {
      const rule = step.configuration.assignmentRule;
      if (rule.type === 'Specific' && !rule.targetUser?.trim())
        row.configuration = 'Target user is required';
      if (rule.type === 'Team' && !rule.targetTeam?.trim())
        row.configuration = 'Target team is required';
      if (rule.type === 'RoundRobin' && (!rule.userPool || rule.userPool.length === 0))
        row.configuration = 'At least one user is required in the pool';
    }

    if (Object.keys(row).length > 0) stepErrors[i] = row;
  });
  if (Object.keys(stepErrors).length > 0) errors.stepErrors = stepErrors;

  return errors;
};

export const isWorkflowValid = (errors: ValidationErrors): boolean =>
  Object.keys(errors).length === 0;
